import {Ref, ref, watch} from 'vue';
import axios, {Method} from 'axios';
import {captureException} from '@sentry/vue';
import {Toast} from 'vant';
import {adapterEnhancer} from '@big-data/react-forge';
import logger, {dataLogger, errorLogger, fastLogger, importantLogger} from '@/utils/logger';
import {getNativeData, notifyPage} from '@/bridge';
import useUa from '@/hooks/useUa';
import {getUrlSearch} from '@/utils/getUrlSearch';
import {timeout, INFO_BASE_URL, SUCCESS_CODE} from './constants';

const {isApp} = useUa();
const {p_client, did} = getUrlSearch();
const http = axios.create({
  baseURL: INFO_BASE_URL,
  timeout,
  headers: {
    'Content-Type': 'application/json',
  },
  adapter: adapterEnhancer(),
});
http.interceptors.request.use(
  config => config,
  err => {
    errorLogger(err);
    captureException(err);
  }
);
http.interceptors.response.use(
  res => {
    notifyPage();
    return res;
  },
  err => {
    errorLogger(err);
    captureException(err);
  }
);

interface AfterRequestParams {
  successInfo?: string;
  errorInfo?: string;
  successCallback?: () => any;
  errorCallback?: (code: number, msg: string) => any;
}

export const processAfterRequest = (params: AfterRequestParams = {}) => (data:any) => {
  const {successInfo, errorInfo, successCallback, errorCallback} = params;
  if (data?.code === SUCCESS_CODE) {
    if (successInfo) {
      Toast.success(successInfo);
    }
    if (successCallback) {
      successCallback();
    }
    dataLogger(data);
    // 取里面的 data
    return data.data;
  }
  // 评论接口错误信息为 msg, 资讯接口为 message
  logger('后端返回错误信息:', '#d85542', `${data?.code}-${data?.message || data?.msg}`);

  if (errorInfo) {// 有默认的错误信息
    Toast.fail(errorInfo);
  }
  if (errorCallback) {// 如果有错误回调
    errorCallback(data?.code, data?.message || data?.msg);
  }
  throw new Error(`${data?.code}#${data?.message || data?.msg}`);
};

const baseProcess = processAfterRequest();

interface IrequestProps {
    method?: Method;
    url: string;
    baseURL?: string;
    useNative?: boolean;
    nativeKey?: string;
    type?: 'immediate' | 'await' | 'manual';
    afterRequest?: (data:any) => any;
    beforeRequest?: (data:any) => any;
    data?: {
        [key: string]: any;
    };
    params?: {
        [key: string]: any;
    };
    headers?: {
        [key: string]: any;
    };
}

export interface UseRequestReturn<T> {
    isFinished: Ref<boolean>;
    error: Ref<any>;
    data: Ref<T | null>;
    isFetching: Ref<boolean>;
    execute: (params?: {[key: string]: any}) => Promise<any>;
}
// @ts-ignore
// eslint-disable-next-line max-lines-per-function
const useRequest: <T>(props: IrequestProps) => UseRequestReturn<T> = props => {
  // type
  // immediate      同步 立即请求
  // await          异步 立即请求
  // manual         同步 不请求
  const {
    method = 'get',
    url,
    useNative,
    nativeKey = '',
    type = 'immediate',
    baseURL,
    data,
    params,
    headers = {},
    afterRequest = baseProcess,
    beforeRequest = (data: any) => data,
  } = props;
  const config: IrequestProps = {
    method,
    url,
    data,
    params: {
      p_client,
      did,
      ...params
    },
    headers
  };
  // eslint-disable-next-line no-undefined
  if (baseURL !== undefined) {
    config.baseURL = baseURL;
  }

  const result = ref<any>(null);
  const first = ref<boolean>(true);
  const isFinished = ref<boolean>(false);
  const isFetching = ref<boolean>(false);
  const error = ref<any>(null);
  // const payload = data || params || ref();
  const execute = async (params = {}) => {
    let type = 'netwodk';
    if (config.method === 'get' || config.method === 'GET') {
      config.params = {
        ...config.params || {},
        ...params
      };
    }
    else {
      config.data = {
        ...config.data || {},
        ...params
      };
    }
    isFetching.value = true;
    isFinished.value = false;
    error.value = null;

    // native和网络中先返回的数据
    const fasterData:any = await new Promise(res => {
      // native预取
      if (isApp && useNative && first.value) {
        // url相同时拼接不同的key以获取不同的数据
        const urlKey = nativeKey ? `?nativeKey=${nativeKey}` : '';
        const nativeUrl = `${baseURL || http.defaults.baseURL}${url}${urlKey}`;
        first.value = false;
        getNativeData(nativeUrl)
          .then(data => {
            if (process.env.NODE_ENV !== 'production') {
              Toast({
                message: `调用数据预取${data ? '成功' : '失败'}`,
                position: 'bottom',
              });
            }
            if (data?.code === SUCCESS_CODE) {
              type = 'native';
              res({data});
            }
          });
      }
      // 网络请求
      http(beforeRequest(config))
        .then(res)
        .catch(err => {
          errorLogger(err);
          res({
            data: {
              code: -1,
              msg: err,
              message: err
            }
          });
          error.value = err || 'error';
          captureException(err);
        });
    });
    fastLogger(fasterData, type);

    const processData = afterRequest(fasterData?.data);
    isFetching.value = false;
    isFinished.value = true;
    result.value = processData;
    return processData;
  };
  if (type === 'await') {
    return execute()
      .then(() => ({
        data: result,
        isFinished,
        isFetching,
        error,
        execute
      }));
  }
  if (type !== 'manual') {
    execute();
  }


  return {
    data: result,
    isFinished,
    isFetching,
    error,
    execute
  };
};

export default useRequest;

